/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
    Trophy,
    Sparkles,
    CheckCircle2,
    XCircle,
    BarChart3,
    TrendingUp,
    Percent,
    Award,
    HelpCircle,
    Calendar,
    ChevronDown,
    ChevronUp,
    AlertCircle
} from 'lucide-react';
import { TRANSLATIONS } from '../translations';

interface PredictionStatsReportProps {
    predictions: Record<string, any>;
    lang?: string;
}

export function PredictionStatsReport({ predictions, lang = 'en' }: PredictionStatsReportProps) {
    const [isOpen, setIsOpen] = useState(true);
    const [activeMetricTab, setActiveMetricTab] = useState<'all' | 'outcome' | 'score' | 'goals' | 'corners'>('all');

    // Translations matching app locale
    const getLang = () => {
        if (lang && lang in TRANSLATIONS) return lang;
        return 'en';
    };
    const activeLang = getLang();
    const t = (key: string, fallback: string) => TRANSLATIONS[activeLang]?.[key] || TRANSLATIONS['en']?.[key] || fallback;

    // Compute stats
    const stats = useMemo(() => {
        const settledList = Object.values(predictions).filter((p: any) => p && p.resolved);
        const activeList = Object.values(predictions).filter((p: any) => p && !p.resolved);

        let totalPoints = 0;
        let totalPacks = 0;

        let correctOutcomes = 0;
        let correctScores = 0;
        let correctGoals = 0;
        let correctCorners = 0;

        settledList.forEach((p: any) => {
            // Accumulate points and packs if present
            totalPoints += (p.pointsWon ?? 0);
            totalPacks += (p.packsWon ?? 0);

            // 1. Exact score
            const isScoreCorrect = (p.actualScoreA === p.scoreA && p.actualScoreB === p.scoreB);
            if (isScoreCorrect) correctScores++;

            // 2. Outright Outcome
            const predDiff = p.scoreA - p.scoreB;
            const actualDiff = p.actualScoreA - p.actualScoreB;
            const isOutcomeCorrect = (predDiff > 0 && actualDiff > 0) ||
                (predDiff < 0 && actualDiff < 0) ||
                (predDiff === 0 && actualDiff === 0);
            if (isOutcomeCorrect) correctOutcomes++;

            // 3. Goals Over Under 2.5
            const actualOverUnder = (p.actualScoreA + p.actualScoreB) > 2.5 ? 'over' : 'under';
            const predOverUnder = p.goalsOverUnder || (p.scoreA + p.scoreB > 2 ? 'over' : 'under');
            const isGoalsCorrect = predOverUnder === actualOverUnder;
            if (isGoalsCorrect) correctGoals++;

            // 4. Corners Guess (within 1)
            const isCornersCorrect = Math.abs(p.corners - p.actualCorners) <= 1;
            if (isCornersCorrect) correctCorners++;
        });

        const totalSettled = settledList.length;
        const totalForecasts = totalSettled * 4; // Each settled match has 4 dimensions of forecasting
        const totalCorrectForecasts = correctOutcomes + correctScores + correctGoals + correctCorners;
        const totalWrongForecasts = totalForecasts - totalCorrectForecasts;

        const overallAccuracyPercent = totalForecasts > 0 ? Math.round((totalCorrectForecasts / totalForecasts) * 100) : 0;
        const overallWrongPercent = totalForecasts > 0 ? (100 - overallAccuracyPercent) : 0;

        const outcomeAccuracyPercent = totalSettled > 0 ? Math.round((correctOutcomes / totalSettled) * 100) : 0;
        const scoreAccuracyPercent = totalSettled > 0 ? Math.round((correctScores / totalSettled) * 100) : 0;
        const goalsAccuracyPercent = totalSettled > 0 ? Math.round((correctGoals / totalSettled) * 100) : 0;
        const cornersAccuracyPercent = totalSettled > 0 ? Math.round((correctCorners / totalSettled) * 100) : 0;

        // Get commentator smart tips
        let analystCommentary = '';
        if (totalSettled === 0) {
            analystCommentary = activeLang === 'es'
                ? 'Aún no tienes predicciones resueltas. ¡Predice los próximos partidos y simula o espera los resultados oficiales para ver tu informe!'
                : 'No settled predictions yet. Try submitting some forecasts and simulates or wait for live games to complete to view your premium analyst report!';
        } else {
            const avg = overallAccuracyPercent;
            if (avg >= 75) {
                analystCommentary = activeLang === 'es'
                    ? '🌟 ¡Nivel Élite! Tu análisis táctico es de clase mundial. Estás superando los modelos de apuestas más rigurosos.'
                    : '🌟 Elite Tactician Level! Your football foresight reads the pitch perfectly. You are outperforming mainstream prediction models.';
            } else if (avg >= 50) {
                analystCommentary = activeLang === 'es'
                    ? '📈 Racha Confiable: Buen ojo para el fútbol ofensivo y las dinámicas de córners. ¡Sigue así y refina tus marcadores!'
                    : '📈 Consistent Performer: Strong intuition for game momentum and corner margins. Keep tracking trends to break past 75%!';
            } else {
                analystCommentary = activeLang === 'es'
                    ? '⚽ ¡El balón es redondo! El fútbol es impredecible, pero tu perseverancia dará frutos. ¡Consulta las estadísticas cara a cara antes de predecir!'
                    : '⚽ The pitch is unpredictable! Football defies the odds, but your analytical breakthrough is around the corner. Study Head-to-Head stats!';
            }
        }

        return {
            totalSubmitted: Object.keys(predictions).length,
            totalSettled,
            totalActive: activeList.length,
            totalPoints,
            totalPacks,
            correctOutcomes,
            correctScores,
            correctGoals,
            correctCorners,
            totalForecasts,
            totalCorrectForecasts,
            totalWrongForecasts,
            overallAccuracyPercent,
            overallWrongPercent,
            outcomeAccuracyPercent,
            scoreAccuracyPercent,
            goalsAccuracyPercent,
            cornersAccuracyPercent,
            analystCommentary
        };
    }, [predictions, activeLang]);

    // Handle circular progress ring properties
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const correctStrokeDashoffset = circumference - (stats.overallAccuracyPercent / 100) * circumference;

    return (
        <div id="user-prediction-report-container" className="bg-[#131d31] border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
            {/* Header bar */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="px-5 py-4 bg-[#1b263b]/40 flex items-center justify-between cursor-pointer transition-colors duration-150 hover:bg-[#1b263b]/70"
            >
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                        <BarChart3 size={18} />
                    </div>
                    <div>
                        <h3 className="text-sm font-black text-white tracking-wide uppercase flex items-center gap-1.5 font-sans">
                            {activeLang === 'es' ? 'Informe de Rendimiento de Predicciones' : 'Prediction Performance Summary Report'}
                            {stats.totalSettled > 0 && (
                                <span className="bg-emerald-500 text-[#090d16] font-mono text-[9px] px-1.5 py-0.5 rounded-full font-black animate-pulse">
                  {stats.overallAccuracyPercent}% {activeLang === 'es' ? 'Acierto' : 'Accuracy'}
                </span>
                            )}
                        </h3>
                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                            {activeLang === 'es'
                                ? `Análisis de ${stats.totalSettled} partidos resueltos (${stats.totalActive} activos)`
                                : `Analytic breakdown of ${stats.totalSettled} settled matches (${stats.totalActive} active)`}
                        </p>
                    </div>
                </div>
                <div className="text-slate-400">
                    {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                        <div className="p-5 border-t border-slate-800/60 space-y-6">

                            {/* Analyst Commentary Box */}
                            <div className="p-4 bg-indigo-950/20 border border-indigo-505/10 rounded-xl flex gap-3 items-start relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-1">
                  <span className="text-[8px] bg-indigo-650/40 text-indigo-300 font-mono px-1.5 py-0.5 rounded border border-indigo-500/20 uppercase font-black">
                    AI Analyst
                  </span>
                                </div>
                                <div className="h-8 w-8 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 shadow-inner mt-0.5 text-xs">
                                    🔮
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-xs text-slate-300 leading-relaxed italic pr-12">
                                        "{stats.analystCommentary}"
                                    </p>
                                </div>
                            </div>

                            {stats.totalSettled === 0 ? (
                                /* Empty state when no predictions are settled yet */
                                <div className="text-center py-6 px-4 bg-slate-900/20 border border-slate-800/40 rounded-xl space-y-3">
                                    <AlertCircle className="mx-auto text-slate-500" size={28} />
                                    <p className="text-xs text-slate-400 max-w-md mx-auto">
                                        {activeLang === 'es'
                                            ? 'Tus estadísticas de aciertos y errores se calcularán en tiempo real tan pronto como un partido predicho termine.'
                                            : 'Your correct guess rates and error ratios will be parsed instantly here once any of your predicted games are settled!'}
                                    </p>
                                    <div className="flex justify-center gap-4 text-[11.5px] font-mono text-slate-500 pt-1.5">
                                        <div>
                                            <span className="text-slate-300 font-bold block">{stats.totalSubmitted}</span>
                                            <span>{activeLang === 'es' ? 'Predicciones' : 'Forecasts'}</span>
                                        </div>
                                        <div className="border-r border-slate-800 h-8 self-center" />
                                        <div>
                                            <span className="text-indigo-400 font-bold block">{stats.totalActive}</span>
                                            <span>{activeLang === 'es' ? 'Pendientes' : 'Pending'}</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Performance metrics content */
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                                    {/* Left Column: Overall Guessed and Wrong Wheel Chart */}
                                    <div className="lg:col-span-5 bg-slate-900/30 rounded-xl border border-slate-800/50 p-5 flex flex-col md:flex-row items-center justify-center gap-6">
                                        {/* SVG Progress Ring */}
                                        <div className="relative h-28 w-28 flex items-center justify-center shrink-0">
                                            <svg className="w-full h-full transform -rotate-90">
                                                {/* Background track circle */}
                                                <circle
                                                    cx="56"
                                                    cy="56"
                                                    r={radius}
                                                    stroke="#1e293b"
                                                    strokeWidth="8"
                                                    fill="transparent"
                                                />
                                                {/* Correct forecast progress circle */}
                                                <circle
                                                    cx="56"
                                                    cy="56"
                                                    r={radius}
                                                    stroke="#10b981"
                                                    strokeWidth="8"
                                                    fill="transparent"
                                                    strokeDasharray={circumference}
                                                    strokeDashoffset={correctStrokeDashoffset}
                                                    className="transition-all duration-1000 ease-out"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                            {/* Percent text in center */}
                                            <div className="absolute text-center">
                        <span className="text-2xl font-black text-white font-mono leading-none">
                          {stats.overallAccuracyPercent}%
                        </span>
                                                <span className="block text-[8px] text-emerald-400 font-mono uppercase tracking-widest font-black leading-none mt-1">
                          {activeLang === 'es' ? 'Correcto' : 'Correct'}
                        </span>
                                            </div>
                                        </div>

                                        <div className="space-y-3 flex-1 w-full font-mono text-xs">
                                            <h4 className="text-[10px] text-slate-400 uppercase tracking-wider font-extrabold pb-1 border-b border-slate-800">
                                                {activeLang === 'es' ? 'Resultados del Sub-Pronóstico' : 'Sub-Forecast Outcomes'}
                                            </h4>
                                            <div className="space-y-2">
                                                {/* Guessed percentage */}
                                                <div>
                                                    <div className="flex justify-between text-[11px] mb-1">
                            <span className="flex items-center gap-1.5 text-emerald-400 font-bold">
                              <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                {activeLang === 'es' ? 'Aciertos (Correcto)' : 'Correctly Guessed'}
                            </span>
                                                        <span className="font-extrabold text-white">
                              {stats.totalCorrectForecasts} / {stats.totalForecasts} ({stats.overallAccuracyPercent}%)
                            </span>
                                                    </div>
                                                </div>
                                                {/* Wrong percentage */}
                                                <div>
                                                    <div className="flex justify-between text-[11px] mb-1">
                            <span className="flex items-center gap-1.5 text-slate-500 font-bold">
                              <span className="h-2 w-2 rounded-full bg-slate-600" />
                                {activeLang === 'es' ? 'Uvas (Incorrecto)' : 'Wrong Predictions'}
                            </span>
                                                        <span className="font-extrabold text-slate-400">
                              {stats.totalWrongForecasts} / {stats.totalForecasts} ({stats.overallWrongPercent}%)
                            </span>
                                                    </div>
                                                </div>
                                                {/* Overall Ratio Bar */}
                                                <div className="h-2 bg-slate-800 rounded-full overflow-hidden flex w-full mt-2.5">
                                                    <div
                                                        style={{ width: `${stats.overallAccuracyPercent}%` }}
                                                        className="bg-emerald-500 transition-all duration-700"
                                                    />
                                                    <div
                                                        style={{ width: `${stats.overallWrongPercent}%` }}
                                                        className="bg-slate-600 transition-all duration-700"
                                                    />
                                                </div>
                                                <div className="text-[8px] text-slate-500 mt-1 max-w-xs text-center md:text-left leading-tight">
                                                    {activeLang === 'es'
                                                        ? '* Cada partido tiene 4 pronósticos: 1) Ganador/Empate, 2) Marcador exacto, 3) Más/Menos de 2.5 goles, 4) Cantidad de córners.'
                                                        : '* Statistics calculated over 4 dimensions per match: 1) Outright Outcome, 2) Exact Score, 3) Over/Under 2.5 goals, 4) Total Corners.'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column: Key dimension accuracy list with custom visual bar reports */}
                                    <div className="lg:col-span-7 flex flex-col justify-between space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">

                                            {/* Card 1: Outright Outcome */}
                                            <div className="p-3.5 bg-slate-900/25 border border-slate-800/40 rounded-xl flex flex-col justify-between space-y-2">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="text-[9px] text-slate-400 uppercase font-mono tracking-wider font-extrabold">Outright Outcome</span>
                                                        <h5 className="text-white font-black text-xs mt-0.5">{activeLang === 'es' ? 'Resultado 1X2' : 'Match Outcome'}</h5>
                                                    </div>
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-black ${stats.outcomeAccuracyPercent >= 65 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-indigo-500/10 text-indigo-400'}`}>
                            {stats.outcomeAccuracyPercent}%
                          </span>
                                                </div>
                                                {/* Progress */}
                                                <div className="space-y-1">
                                                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden flex">
                                                        <div style={{ width: `${stats.outcomeAccuracyPercent}%` }} className="bg-gradient-to-r from-emerald-500 to-teal-400" />
                                                    </div>
                                                    <div className="flex justify-between text-[10px] font-mono text-slate-400">
                                                        <span>{stats.correctOutcomes} {activeLang === 'es' ? 'Acierto' : 'Correct'}</span>
                                                        <span>{stats.totalSettled - stats.correctOutcomes} {activeLang === 'es' ? 'Errores' : 'Wrong'}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Card 2: Exact Score */}
                                            <div className="p-3.5 bg-slate-900/25 border border-slate-800/40 rounded-xl flex flex-col justify-between space-y-2">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="text-[9px] text-slate-400 uppercase font-mono tracking-wider font-extrabold">Correct Scoreline</span>
                                                        <h5 className="text-white font-black text-xs mt-0.5">{activeLang === 'es' ? 'Marcador Exacto' : 'Exact Score'}</h5>
                                                    </div>
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-black ${stats.scoreAccuracyPercent >= 30 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-indigo-500/10 text-indigo-400'}`}>
                            {stats.scoreAccuracyPercent}%
                          </span>
                                                </div>
                                                {/* Progress */}
                                                <div className="space-y-1">
                                                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden flex">
                                                        <div style={{ width: `${stats.scoreAccuracyPercent}%` }} className="bg-gradient-to-r from-amber-500 to-yellow-400" />
                                                    </div>
                                                    <div className="flex justify-between text-[10px] font-mono text-slate-400">
                                                        <span>{stats.correctScores} {activeLang === 'es' ? 'Acierto' : 'Correct'}</span>
                                                        <span>{stats.totalSettled - stats.correctScores} {activeLang === 'es' ? 'Errores' : 'Wrong'}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Card 3: Goals O/U 2.5 */}
                                            <div className="p-3.5 bg-slate-900/25 border border-slate-800/40 rounded-xl flex flex-col justify-between space-y-2">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="text-[9px] text-slate-400 uppercase font-mono tracking-wider font-extrabold">Goals Over/Under</span>
                                                        <h5 className="text-white font-black text-xs mt-0.5">{activeLang === 'es' ? 'Goles +/- 2.5' : 'Goals O/U 2.5'}</h5>
                                                    </div>
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-black ${stats.goalsAccuracyPercent >= 65 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-indigo-500/10 text-indigo-400'}`}>
                            {stats.goalsAccuracyPercent}%
                          </span>
                                                </div>
                                                {/* Progress */}
                                                <div className="space-y-1">
                                                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden flex">
                                                        <div style={{ width: `${stats.goalsAccuracyPercent}%` }} className="bg-gradient-to-r from-indigo-500 to-blue-400" />
                                                    </div>
                                                    <div className="flex justify-between text-[10px] font-mono text-slate-400">
                                                        <span>{stats.correctGoals} {activeLang === 'es' ? 'Acierto' : 'Correct'}</span>
                                                        <span>{stats.totalSettled - stats.correctGoals} {activeLang === 'es' ? 'Errores' : 'Wrong'}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Card 4: Corners Accuracy */}
                                            <div className="p-3.5 bg-slate-900/25 border border-slate-800/40 rounded-xl flex flex-col justify-between space-y-2">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="text-[9px] text-slate-400 uppercase font-mono tracking-wider font-extrabold">Corners Range (±1)</span>
                                                        <h5 className="text-white font-black text-xs mt-0.5">{activeLang === 'es' ? 'Rango de Córners' : 'Corners Accuracy'}</h5>
                                                    </div>
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-black ${stats.cornersAccuracyPercent >= 60 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-indigo-500/10 text-indigo-400'}`}>
                            {stats.cornersAccuracyPercent}%
                          </span>
                                                </div>
                                                {/* Progress */}
                                                <div className="space-y-1">
                                                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden flex">
                                                        <div style={{ width: `${stats.cornersAccuracyPercent}%` }} className="bg-gradient-to-r from-purple-500 to-pink-400" />
                                                    </div>
                                                    <div className="flex justify-between text-[10px] font-mono text-slate-400">
                                                        <span>{stats.correctCorners} {activeLang === 'es' ? 'Acierto' : 'Correct'}</span>
                                                        <span>{stats.totalSettled - stats.correctCorners} {activeLang === 'es' ? 'Errores' : 'Wrong'}</span>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>

                                        {/* Bottom row summary badges */}
                                        <div className="flex flex-wrap gap-2.5 pt-2 border-t border-slate-800/60 font-mono text-[10px]">
                                            <div className="bg-[#1e293b]/50 border border-slate-800 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                                                <Trophy size={11} className="text-amber-400" />
                                                <span className="text-slate-400">{activeLang === 'es' ? 'Puntos Totales:' : 'Points Won:'} <strong className="text-amber-400 font-extrabold">{stats.totalPoints} PTS</strong></span>
                                            </div>
                                            <div className="bg-[#1e293b]/50 border border-slate-800 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                                                <Sparkles size={11} className="text-yellow-400 animate-pulse" />
                                                <span className="text-slate-400">{activeLang === 'es' ? 'Sobres Ganados:' : 'Packs Earned:'} <strong className="text-yellow-400 font-extrabold">{stats.totalPacks} 🎁</strong></span>
                                            </div>
                                        </div>

                                    </div>

                                </div>
                            )}

                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
